import { useDocumentTitle } from '@refinedev/react-router-v6';
import { useNotification } from '@refinedev/core';
import { useCreatePatient, useUpdatePatient } from 'api/resource';
import {
  CreateMutationDataParams,
  UpdateMutationDataParams,
  updateMutationData,
} from 'api/utils/MutationDataBuilder';
import { PatientColumns } from 'columns/Patient';
import {
  BillingDataGridWrapper,
  CreateRowButton,
  EditRowButton,
  TableAlertBanner,
} from 'components/common';
import { Patient } from 'interfaces';
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_Row,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_TableOptions,
} from 'mantine-react-table';
import { z } from 'zod';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  Alert,
  Box,
  Stack,
  Button,
  Flex,
  Loader,
  useComputedColorScheme,
} from '@mantine/core';
import { basePatientSchema } from '@billing/validation/basePatient';
import { patientSchema } from '@billing/validation';
import { useCanModify } from 'hooks';
import * as Sentry from '@sentry/react';
import { buttonTextColor } from 'utils/buttonUtils';
// import { useCurrentSiteStore } from 'store/SiteStore';

interface PatientManagementProps {
  patients: Patient[];
  total: number;
  nextPage: number;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  errorMessage: string;
  pagination: {
    pageIndex: number;
    pageSize: number;
    globalFilter: string | undefined;
    columnFilters: MRT_ColumnFiltersState;
    sorting: MRT_SortingState;
  };
  setPagination: Dispatch<
    SetStateAction<{
      pageIndex: number;
      pageSize: number;
      globalFilter: string | undefined;
      columnFilters: MRT_ColumnFiltersState;
      sorting: MRT_SortingState;
    }>
  >;
}

const extractValidationMessages = (error: z.ZodError<Patient>): string[] => {
  const messages: string[] = [];
  if (error) {
    const tree = z.treeifyError(error);
    if (tree) {
      for (const [field, info] of Object.entries(tree.properties || {})) {
        const fieldErrs = info.errors;
        if (Array.isArray(fieldErrs) && fieldErrs.length)
          messages.push(`${field}: ${fieldErrs.join(', ')}`);
      }
      if (Array.isArray(tree.errors) && tree.errors.length)
        messages.push(...tree.errors);
    }
  }
  return messages.length ? messages : ['Please fix the validation errors.'];
};

export const PatientManagement: React.FC<PatientManagementProps> = ({
  patients,
  total,
  isLoading,
  isError,
  isFetching,
  errorMessage,
  pagination,
  setPagination,
}) => {
  const title = 'Patient Management';
  useDocumentTitle('Patients | QBilling');
  // const { organizationID } = useCurrentSiteStore();

  const { canModify } = useCanModify('patients');
  const { open } = useNotification();
  const createPatient = useCreatePatient();
  const updatePatient = useUpdatePatient();
  const data: Patient[] = patients ?? [];
  const columns: MRT_ColumnDef<Patient>[] = PatientColumns();

  const [creationErrors, setCreationErrors] = useState<string[]>([]);
  const [editErrors, setEditErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const colorScheme = useComputedColorScheme();

  const handleCreatePatient = async ({
    table,
    values,
  }: CreateMutationDataParams<Patient>) => {
    setLoading(true);
    try {
      const validation = await basePatientSchema
        .omit({ id: true, searchVector: true })
        .safeParseAsync(values);
      if (!validation.success) {
        setCreationErrors(extractValidationMessages(validation.error as any));
        return;
      }
      setCreationErrors([]);
      const result = await createPatient.mutateAsync(values);
      if (result?.error) {
        setCreationErrors([result.error]);
        return;
      }
      open({
        message: 'Patient Created',
        description: 'Patient has been created successfully.',
        type: 'success',
      });
      table?.setCreatingRow(null);
    } catch (error) {
      // sentry log
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePatient = async ({
    table,
    values,
    row,
  }: UpdateMutationDataParams<Patient>) => {
    setLoading(true);
    try {
      const validation = await patientSchema
        .omit({ id: true, searchVector: true })
        .safeParseAsync(values);
      if (!validation.success) {
        setEditErrors(extractValidationMessages(validation.error as any));
        return;
      }
      setEditErrors([]);
      await updateMutationData<Patient>({
        table,
        values,
        row,
        mutation: updatePatient,
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  const tableOptions: MRT_TableOptions<Patient> = {
    data,
    columns,
    createDisplayMode: 'modal',
    initialState: {
      globalFilter: pagination.globalFilter,
      columnFilters: pagination.columnFilters,
      sorting: pagination.sorting,
    },
    state: {
      pagination,
      globalFilter: pagination.globalFilter,
      columnFilters: pagination.columnFilters,
      sorting: pagination.sorting,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching || isLoading,
      columnVisibility: {
        city: false,
        address: false,
        country: false,
        postalCode: false,
        phone: false,
        email: false,
        organizationID: false,
      },
    },
    rowCount: total,
    manualFiltering: true,
    manualPagination: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: (updater) =>
      setPagination((prev) => ({
        ...prev,
        columnFilters:
          typeof updater === 'function' ? updater(prev.columnFilters) : updater,
      })),
    onGlobalFilterChange: (value) =>
      setPagination((prev) => ({
        ...prev,
        globalFilter: value,
      })),
    onSortingChange: (updater) =>
      setPagination((prev) => ({
        ...prev,
        sorting:
          typeof updater === 'function' ? updater(prev.sorting) : updater,
      })),
    enableEditing: true,
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: errorMessage });
    },
    mantineCreateRowModalProps: {
      withCloseButton: true,
      title: 'Add New Patient',
      onClose: () => setCreationErrors([]),
    },
    onCreatingRowSave: handleCreatePatient,
    onEditingRowSave: handleUpdatePatient,
    renderTopToolbarCustomActions: ({ table }) =>
      CreateRowButton<Patient>({
        table,
        type: 'patient',
        disabled: isLoading || !canModify,
        isLoading: isLoading,
      }),
    renderCreateRowModalContent: ({ table, row, internalEditComponents }) => (
      <Stack gap="sm" p="sm">
        {isLoading && <Loader size="lg" variant="dots" mx="auto" />}
        {creationErrors.length > 0 && (
          <Alert
            variant="light"
            color="red"
            title="Validation Errors"
            withCloseButton
            onClose={() => setCreationErrors([])}
            ref={(el) => {
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }}
          >
            <Stack gap={4}>
              {creationErrors.map((err, i) => (
                <Box key={i}>{err}</Box>
              ))}
            </Stack>
          </Alert>
        )}
        {internalEditComponents}
        <Flex justify={'center'} gap={'lg'} style={{ marginTop: '1%' }}>
          <Button
            color="gray"
            c={buttonTextColor(colorScheme, loading)}
            onClick={() => {
              setCreationErrors([]);
              table.setCreatingRow(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="green"
            c={buttonTextColor(colorScheme, loading)}
            disabled={loading}
            onClick={async () => {
              await handleCreatePatient({
                table,
                values: { ...(row?._valuesCache || {}) } as Patient,
              });
            }}
          >
            Create
          </Button>
        </Flex>
      </Stack>
    ),
    renderEditRowModalContent: ({ table, row, internalEditComponents }) => (
      <Stack gap="sm" p="sm">
        {editErrors.length > 0 && (
          <Alert
            variant="light"
            color="red"
            title="Validation Errors"
            withCloseButton
            onClose={() => setEditErrors([])}
            ref={(el) => {
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }}
          >
            <Stack gap={4}>
              {editErrors.map((err, i) => (
                <Box key={i}>{err}</Box>
              ))}
            </Stack>
          </Alert>
        )}
        {internalEditComponents}
        <Flex justify={'center'} gap={'lg'} style={{ marginTop: '1%' }}>
          <Button
            color="gray"
            c={buttonTextColor(colorScheme, loading)}
            onClick={() => {
              setEditErrors([]);
              table.setEditingRow(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="green"
            c={buttonTextColor(colorScheme, loading)}
            disabled={loading}
            onClick={async () => {
              await handleUpdatePatient({
                table,
                values: { ...(row?._valuesCache || {}) } as Patient,
                row,
              });
            }}
          >
            Save
          </Button>
        </Flex>
      </Stack>
    ),
    displayColumnDefOptions: {
      'mrt-row-actions': {
        Cell: ({
          table,
          row,
        }: {
          table: MRT_TableInstance<Patient>;
          row: MRT_Row<Patient>;
        }) => (
          <EditRowButton
            table={table}
            row={row}
            type="patient"
            disabled={!canModify}
          />
        ),
      },
    },
    mantineEditRowModalProps: {
      size: 'xl',
    },
  };
  return (
    <BillingDataGridWrapper
      title={title}
      tableOptions={tableOptions}
      dataTestID="reportManagement"
    />
  );
};
